import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getWithExpiry } from '../../../Domain/Helpers/LocalStorageHelper';
import { getAggregators } from '../../../Infrastructure/Services/AggregatorService';
import { getProviders, importMissingProviders, getProviderAliases, updateProviderAliases, getDbProviders, deleteProviderAlias } from '../../../Infrastructure/Services/ProviderService';
import '../../App.css';

/* ── Role helper ── */
function useIsGisAdmin() {
  const roles = getWithExpiry('roles') ?? [];
  return Array.isArray(roles) ? roles.includes('gis-admin') : String(roles).includes('gis-admin');
}

/* ── Badges ── */
function BoolBadge({ val, trueLabel = 'Yes', falseLabel = 'No' }) {
  if (val === true || val === 1)
    return <span className="badge bg-success">{trueLabel}</span>;
  if (val === false || val === 0)
    return <span className="badge bg-warning text-dark">{falseLabel}</span>;
  return <span className="badge bg-light text-dark border">—</span>;
}

function StatusBadge({ p }) {
  if (!p.is_in_db)
    return <span className="badge bg-warning text-dark">Not in DB</span>;
  if (!p.is_active || p.is_active === false || p.is_active === 0)
    return <span className="badge bg-danger">Inactive</span>;
  return <span className="badge bg-success">Active</span>;
}

/* ── Import Result Banner ── */
function ImportBanner({ result, onClose }) {
  if (!result) return null;
  const created   = result.created?.length   ?? result.created   ?? 0;
  const activated = result.activated?.length ?? result.activated ?? 0;
  return (
    <div className="alert alert-success d-flex justify-content-between align-items-center mb-3" role="alert">
      <span>
        Import complete —{' '}
        <strong>{created}</strong> created,{' '}
        <strong>{activated}</strong> activated.
      </span>
      <button type="button" className="btn-close" onClick={onClose} />
    </div>
  );
}

/* ── Searchable dropdown (mirrors CasinoGamesPage's SearchableSelect) ── */
function SearchableSelect({ value, onChange, disabled, options, placeholder = '— Select —', labelKey = 'label', valueKey = 'value' }) {
  const [search, setSearch] = React.useState('');
  const [open, setOpen]     = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  React.useEffect(() => { if (!open) setSearch(''); }, [open]);

  const filtered = options.filter(o =>
    String(o[labelKey]).toLowerCase().includes(search.toLowerCase())
  );

  const selectedLabel = options.find(o => String(o[valueKey]).toLowerCase() === String(value).toLowerCase())?.[labelKey];

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 180 }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        style={{
          width: '100%', padding: '5px 30px 5px 10px', fontSize: 13,
          border: '1px solid #dee2e6', borderRadius: 4,
          background: disabled ? '#e9ecef' : '#fff',
          color: value ? '#2e2e31' : '#6c757d',
          cursor: disabled ? 'default' : 'pointer',
          textAlign: 'left', position: 'relative',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedLabel ?? (value || placeholder)}
        </span>
        <span style={{ fontSize: 10, color: '#6c757d', flexShrink: 0, marginLeft: 6 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 2000,
          background: '#fff', border: '1px solid #dee2e6', borderRadius: 4,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', marginTop: 2,
          minWidth: 220,
        }}>
          {/* Search input */}
          <div style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
            <input
              autoFocus
              type="text"
              className="form-control form-control-sm"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
              style={{ fontSize: 12 }}
            />
          </div>
          {/* Options */}
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            <div
              onClick={() => { onChange(''); setOpen(false); }}
              style={{
                padding: '6px 12px', fontSize: 13, cursor: 'pointer',
                color: '#6c757d',
                background: !value ? '#f8f9fa' : 'transparent',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
              onMouseLeave={e => e.currentTarget.style.background = !value ? '#f8f9fa' : 'transparent'}
            >
              {placeholder}
            </div>
            {filtered.length === 0 && (
              <div style={{ padding: '8px 12px', fontSize: 12, color: '#9ca3af' }}>No results</div>
            )}
            {filtered.map(o => (
              <div
                key={o[valueKey]}
                onClick={() => { onChange(String(o[valueKey])); setOpen(false); }}
                style={{
                  padding: '6px 12px', fontSize: 13, cursor: 'pointer',
                  background: String(o[valueKey]).toLowerCase() === String(value).toLowerCase() ? '#fdf2f8' : 'transparent',
                  color: String(o[valueKey]).toLowerCase() === String(value).toLowerCase() ? '#e20075' : '#2e2e31',
                  fontWeight: String(o[valueKey]).toLowerCase() === String(value).toLowerCase() ? 600 : 400,
                }}
                onMouseEnter={e => { if (String(o[valueKey]).toLowerCase() !== String(value).toLowerCase()) e.currentTarget.style.background = '#f8f9fa'; }}
                onMouseLeave={e => { e.currentTarget.style.background = String(o[valueKey]).toLowerCase() === String(value).toLowerCase() ? '#fdf2f8' : 'transparent'; }}
              >
                {o[labelKey]}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Aliases Tab ── */
function AliasesTab({
  aliases, setAliases, loading, saving, onSave, onDeleteRow, deletingIndex,
  aggregators, aliasAggId, setAliasAggId,
  aggProviders, loadingAggProviders,
  dbProviders, loadingDbProviders,
}) {
  const updateRow = (i, field, value) =>
    setAliases(prev => prev.map((row, idx) => idx === i ? { ...row, [field]: value } : row));

  const addRow = () =>
    setAliases(prev => [...prev, { apiName: '', name: '', persisted: false }]);

  const apiNameOptions = aggProviders.map(p => ({ value: p.name, label: p.name }));
  const dbProviderOptions = dbProviders.map(p => ({ value: p.name, label: p.name }));

  return (
    <div className="card-section">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="mb-1" style={{ color: '#2e2e31' }}>Provider Name Aliases</h6>
          <p className="page-subtitle" style={{ margin: 0 }}>
            Map aggregator API names to DB provider names when they don't match exactly.
          </p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={addRow} disabled={loading}>
            + Add Row
          </button>
          <button className="btn btn-flexpink btn-sm" onClick={onSave} disabled={saving || loading || !aliasAggId}>
            {saving
              ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</>
              : 'Save Aliases'
            }
          </button>
        </div>
      </div>

      <div className="filter-group mb-3" style={{ maxWidth: 260 }}>
        <label className="filter-label">Provider Source (Aggregator)</label>
        <select
          className="form-select form-select-sm"
          value={aliasAggId}
          onChange={e => setAliasAggId(e.target.value)}
        >
          <option value="">— Select aggregator —</option>
          {aggregators.map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        <p style={{ fontSize: 10, color: '#9ca3af', margin: '3px 0 0', fontStyle: 'italic' }}>
          Pick which aggregator's providers to search for the API Name and DB Name columns
        </p>
      </div>

      {loading
        ? <div className="text-center p-3"><div className="spinner-border text-secondary" role="status" /></div>
        : aliases.length === 0
          ? <div className="empty-state">
              <div className="empty-state-icon">◈</div>
              <p>No aliases yet — add one to map a mismatched provider name.</p>
            </div>
          : <div>
              <table className="table table-hover table-sm align-middle">
                <thead>
                  <tr>
                    <th>API Name</th>
                    <th>DB Provider Name</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {aliases.map((row, i) => (
                    <tr key={i}>
                      <td>
                        <SearchableSelect
                          value={row.apiName}
                          onChange={v => updateRow(i, 'apiName', v)}
                          disabled={!aliasAggId || loadingAggProviders}
                          options={apiNameOptions}
                          placeholder={loadingAggProviders ? 'Loading…' : '— Select API provider —'}
                        />
                      </td>
                      <td>
                        <SearchableSelect
                          value={row.name}
                          onChange={v => updateRow(i, 'name', v)}
                          disabled={!aliasAggId || loadingDbProviders}
                          options={dbProviderOptions}
                          placeholder={loadingDbProviders ? 'Loading…' : '— Select DB provider —'}
                        />
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => onDeleteRow(i)}
                          disabled={deletingIndex === i}
                        >
                          {deletingIndex === i
                            ? <span className="spinner-border spinner-border-sm" />
                            : '×'
                          }
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
      }
    </div>
  );
}

/* ════════════════════════════════════════
   ProvidersPage
   ════════════════════════════════════════ */
function ProvidersPage({ setLoggedIn }) {
  const isGisAdmin = useIsGisAdmin();

  const [aggregators, setAggregators]           = useState([]);
  const [aggId, setAggId]                       = useState('');
  const [providers, setProviders]               = useState([]);
  const [loadingAggs, setLoadingAggs]           = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [selectedNames, setSelectedNames]       = useState([]);
  const [importing, setImporting]               = useState(false);
  const [providerSearch, setProviderSearch]     = useState('');
  const [importResult, setImportResult]         = useState(null);

  /* Tabs — Aliases tab only exists for gis-admin */
  const [activeTab, setActiveTab]           = useState('providers');
  const [aliases, setAliases]               = useState([]);
  const [loadingAliases, setLoadingAliases] = useState(false);
  const [savingAliases, setSavingAliases]   = useState(false);
  const [deletingIndex, setDeletingIndex]   = useState(null);

  /* Aliases tab — API provider (provider-list) + DB provider (provider-db-list) lookups, scoped by aggregator */
  const [aliasAggId, setAliasAggId]                   = useState('');
  const [aggProviders, setAggProviders]               = useState([]);
  const [loadingAggProviders, setLoadingAggProviders] = useState(false);
  const [dbProviders, setDbProviders]                 = useState([]);
  const [loadingDbProviders, setLoadingDbProviders]   = useState(false);

  const loadAliases = useCallback(async () => {
    if (!aliasAggId) { setAliases([]); return; }
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setLoadingAliases(true);
    try {
      const nested = await getProviderAliases(token);
      const map = nested?.[aliasAggId] ?? {};
      setAliases(Object.entries(map).map(([apiName, name]) => ({ apiName, name, persisted: true })));
    } finally { setLoadingAliases(false); }
  }, [setLoggedIn, aliasAggId]);

  useEffect(() => {
    if (activeTab === 'aliases' && isGisAdmin && aliasAggId) loadAliases();
  }, [activeTab, isGisAdmin, aliasAggId, loadAliases]);

  /* Load API providers (provider-list) whenever the alias-tab aggregator changes */
  useEffect(() => {
    if (!aliasAggId) { setAggProviders([]); return; }
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setLoadingAggProviders(true);
    setAggProviders([]);
    getProviders(token, aliasAggId)
      .then(d => { if (d) setAggProviders(d); })
      .finally(() => setLoadingAggProviders(false));
  }, [aliasAggId, setLoggedIn]);

  /* Load DB-only providers whenever the alias-tab aggregator changes */
  useEffect(() => {
    if (!aliasAggId) { setDbProviders([]); return; }
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setLoadingDbProviders(true);
    setDbProviders([]);
    getDbProviders(token, aliasAggId)
      .then(d => { if (d) setDbProviders(d); })
      .finally(() => setLoadingDbProviders(false));
  }, [aliasAggId, setLoggedIn]);

  const handleSaveAliases = async () => {
    if (!aliasAggId) { toast.warn('Select an aggregator first'); return; }
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    const cleaned = aliases
      .map(a => ({ apiName: a.apiName.trim(), name: a.name.trim() }))
      .filter(a => a.apiName && a.name);
    if (cleaned.length === 0) { toast.warn('Add at least one complete row before saving'); return; }
    setSavingAliases(true);
    try {
      const result = await updateProviderAliases(token, aliasAggId, cleaned);
      if (result) { toast.success('Aliases saved'); await loadAliases(); }
    } finally { setSavingAliases(false); }
  };

  const handleDeleteRow = async (i) => {
    const row = aliases[i];
    if (!row) return;

    if (!row.persisted) {
      setAliases(prev => prev.filter((_, idx) => idx !== i));
      return;
    }

    if (!window.confirm(`Remove alias "${row.apiName}" → "${row.name}"?`)) return;

    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setDeletingIndex(i);
    try {
      const result = await deleteProviderAlias(token, aliasAggId, row.apiName);
      if (result) {
        toast.success('Alias removed');
        setAliases(prev => prev.filter((_, idx) => idx !== i));
      }
    } finally { setDeletingIndex(null); }
  };

  /* Load aggregators once */
  useEffect(() => {
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setLoadingAggs(true);
    getAggregators(token)
      .then(aggs => {
        if (aggs?.length) {
          setAggregators(aggs);
          setAggId(String(aggs[0].id));
        }
      })
      .catch(() => toast.error('Failed to load aggregators'))
      .finally(() => setLoadingAggs(false));
  }, [setLoggedIn]);

  /* Load providers whenever aggId changes */
  const loadProviders = useCallback(async (id) => {
    const currentId = id ?? aggId;
    if (!currentId) return;
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setLoadingProviders(true);
    setProviders([]);
    setSelectedNames([]);
    try {
      const result = await getProviders(token, currentId);
      if (result) setProviders(result);
    } catch { toast.error('Failed to load providers'); }
    finally { setLoadingProviders(false); }
  }, [aggId, setLoggedIn]);

  useEffect(() => {
    if (aggId) loadProviders(aggId);
  }, [aggId]);

  /* Selection — only missing/inactive providers are selectable */
  const isSelectable = (p) =>
    !p.is_in_db || !p.is_active || p.is_active === false || p.is_active === 0;

  const selectableProviders = providers.filter(isSelectable);
  const filteredProviders = providerSearch.trim()
    ? providers.filter(p => p.name.toLowerCase().includes(providerSearch.toLowerCase()))
    : providers;
  const allSelected = selectableProviders.length > 0 && selectedNames.length === selectableProviders.length;

  const toggleName = (name) =>
    setSelectedNames(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);

  const toggleAll = () =>
    setSelectedNames(allSelected ? [] : selectableProviders.map(p => p.name));

  /* Import */
  const handleImport = async () => {
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setImporting(true);
    try {
      // selectedNames empty OR all missing selected → import all (send null)
      const namesToSend = selectedNames.length > 0 && selectedNames.length < selectableProviders.length
        ? selectedNames
        : null;
      const result = await importMissingProviders(token, aggId, namesToSend);
      if (result) {
        setImportResult(result);
        setSelectedNames([]);
        await loadProviders(aggId);
      }
    } catch { toast.error('Import failed'); }
    finally { setImporting(false); }
  };

  /* Summary counts */
  const notInDbCount  = providers.filter(p => !p.is_in_db).length;
  const inactiveCount = providers.filter(p => p.is_in_db && (!p.is_active || p.is_active === 0 || p.is_active === false)).length;

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h4 className="page-title">Providers</h4>
          <p className="page-subtitle">View and import providers from aggregators</p>
        </div>
      </div>

      <ImportBanner result={importResult} onClose={() => setImportResult(null)} />

      {/* Tab bar — gis-admin only */}
      {isGisAdmin && (
        <div className="tab-bar" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button
            className={`btn btn-sm ${activeTab === 'providers' ? 'btn-fadeblack' : 'btn-outline-secondary'}`}
            onClick={() => setActiveTab('providers')}
          >
            Providers
          </button>
          <button
            className={`btn btn-sm ${activeTab === 'aliases' ? 'btn-fadeblack' : 'btn-outline-secondary'}`}
            onClick={() => setActiveTab('aliases')}
          >
            Name Aliases
          </button>
        </div>
      )}

      {/* Aliases tab */}
      {isGisAdmin && (
        <div style={{ display: activeTab === 'aliases' ? 'block' : 'none' }}>
          <AliasesTab
            aliases={aliases}
            setAliases={setAliases}
            loading={loadingAliases}
            saving={savingAliases}
            onSave={handleSaveAliases}
            onDeleteRow={handleDeleteRow}
            deletingIndex={deletingIndex}
            aggregators={aggregators}
            aliasAggId={aliasAggId}
            setAliasAggId={setAliasAggId}
            aggProviders={aggProviders}
            loadingAggProviders={loadingAggProviders}
            dbProviders={dbProviders}
            loadingDbProviders={loadingDbProviders}
          />
        </div>
      )}

      {/* Providers tab */}
      <div style={{ display: !isGisAdmin || activeTab === 'providers' ? 'block' : 'none' }}>
        {/* Step 1 — Aggregator selector */}
        <div className="card-section">
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
            <div className="filter-group">
              <label className="filter-label">Aggregator</label>
              <select
                className="form-select form-select-sm"
                style={{ minWidth: 200 }}
                value={aggId}
                onChange={e => setAggId(e.target.value)}
                disabled={loadingAggs}
              >
                <option value="">— Select aggregator —</option>
                {aggregators.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>

            <button
              className="btn btn-fadeblack btn-sm"
              onClick={() => loadProviders(aggId)}
              disabled={loadingProviders || !aggId}
            >
              {loadingProviders
                ? <><span className="spinner-border spinner-border-sm me-1" />Loading…</>
                : '↻ Refresh'
              }
            </button>
          </div>
        </div>

        {/* Provider table */}
        {(providers.length > 0 || loadingProviders) && (
          <div className="card-section">
            {/* Table header row */}
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <h6 className="mb-1" style={{ color: '#2e2e31' }}>
                  {providers.length} providers
                </h6>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ width: 200, fontSize: 12 }}
                  placeholder="Search providers…"
                  value={providerSearch}
                  onChange={e => setProviderSearch(e.target.value)}
                />
                {(notInDbCount > 0 || inactiveCount > 0) && (
                  <p style={{ fontSize: 12, color: '#d97706', margin: 0 }}>
                    {notInDbCount > 0 && <span>{notInDbCount} not in DB</span>}
                    {notInDbCount > 0 && inactiveCount > 0 && <span> · </span>}
                    {inactiveCount > 0 && <span>{inactiveCount} inactive</span>}
                  </p>
                )}
              </div>

              {/* Import button — gis-admin only */}
              {isGisAdmin && selectableProviders.length > 0 && (
                <div className="d-flex align-items-center gap-2">
                  {selectableProviders.length > 0 && (
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={toggleAll}
                      disabled={importing}
                    >
                      {allSelected
                        ? 'Deselect All'
                        : `Select All Missing / Inactive (${selectableProviders.length})`
                      }
                    </button>
                  )}
                  <button
                    className="btn btn-flexpink btn-sm"
                    onClick={handleImport}
                    disabled={importing || !aggId}
                  >
                    {importing
                      ? <><span className="spinner-border spinner-border-sm me-1" />Importing…</>
                      : selectedNames.length > 0 && selectedNames.length < selectableProviders.length
                        ? `⊕ Import Selected (${selectedNames.length})`
                        : `⊕ Import All Missing (${selectableProviders.length})`
                    }
                  </button>
                </div>
              )}
            </div>

            {loadingProviders
              ? <div className="text-center p-3"><div className="spinner-border text-secondary" role="status" /></div>
              : <div style={{ overflowX: 'auto' }}>
                  <table className="table table-hover table-sm align-middle">
                    <thead>
                      <tr>
                        {/* Checkbox column — gis-admin only, only if there are selectable providers */}
                        {isGisAdmin && selectableProviders.length > 0 && (
                          <th style={{ width: 36 }}>
                            <input
                              type="checkbox"
                              title="Select all missing / inactive"
                              checked={allSelected}
                              onChange={toggleAll}
                            />
                          </th>
                        )}
                        <th>ID</th>
                        <th>Name</th>
                        <th>Redirect Path</th>
                        <th>Aggregator ID</th>
                        <th>In DB</th>
                        <th>Active at Aggregator</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProviders.map((p, i) => {
                        const selectable = isSelectable(p);
                        const sel        = selectedNames.includes(p.name);
                        return (
                          <tr
                            key={p.id ?? `missing-${i}`}
                            onClick={() => isGisAdmin && selectable && toggleName(p.name)}
                            style={{
                              cursor:     isGisAdmin && selectable ? 'pointer' : 'default',
                              background: sel       ? '#fdf2f8'
                                        : selectable ? '#fffbeb'
                                        : undefined,
                            }}
                          >
                            {/* Checkbox cell */}
                            {isGisAdmin && selectableProviders.length > 0 && (
                              <td onClick={e => e.stopPropagation()}>
                                {selectable && (
                                  <input
                                    type="checkbox"
                                    checked={sel}
                                    onChange={() => toggleName(p.name)}
                                  />
                                )}
                              </td>
                            )}

                            <td>
                              {p.id != null
                                ? <span className="badge bg-secondary">{p.id}</span>
                                : <span className="text-muted">—</span>
                              }
                            </td>
                            <td>
                              <strong style={{ fontSize: 13 }}>{p.name}</strong>
                              {selectable && (
                                <span style={{ fontSize: 10, color: '#d97706', marginLeft: 6 }}>
                                  {!p.is_in_db ? 'not in DB' : 'inactive'}
                                </span>
                              )}
                            </td>
                            <td>
                              {p.redirect_path
                                ? <code style={{ fontSize: 11 }}>{p.redirect_path}</code>
                                : <span className="text-muted">—</span>
                              }
                            </td>
                            <td>
                              <span className="badge bg-light text-dark border">{p.agregator_id}</span>
                            </td>
                            <td><BoolBadge val={p.is_in_db} /></td>
                            <td><BoolBadge val={p.is_in_aggregator} /></td>
                            <td><StatusBadge p={p} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Legend */}
                  <div style={{ display: 'flex', gap: 20, marginTop: 8, fontSize: 11, color: '#6b7280' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 10, height: 10, background: '#fffbeb', border: '1px solid #fde68a', display: 'inline-block', borderRadius: 2 }} />
                      Missing or inactive
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 10, height: 10, background: '#fdf2f8', border: '1px solid #f9a8d4', display: 'inline-block', borderRadius: 2 }} />
                      Selected for import
                    </span>
                  </div>
                </div>
            }
          </div>
        )}

        {/* Empty / prompt state */}
        {!loadingProviders && providers.length === 0 && aggId && (
          <div className="card-section">
            <div className="empty-state">
              <div className="empty-state-icon">◈</div>
              <p>No providers found for this aggregator.</p>
              {isGisAdmin && (
                <button className="btn btn-flexpink btn-sm mt-2" onClick={handleImport} disabled={importing}>
                  Import All Missing
                </button>
              )}
            </div>
          </div>
        )}

        {!aggId && !loadingAggs && (
          <div className="card-section">
            <div className="empty-state">
              <div className="empty-state-icon">◈</div>
              <p>Select an aggregator to view its providers</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProvidersPage;
